/**
 * Strips oversized user-authored bodies from a session snapshot at PERSIST time.
 *
 * The in-memory `currentSession` / `savedSessions` keep full bodies, so
 * switching sessions within a run never loses a script. Only the localStorage
 * snapshot (zustand `partialize`) is capped. The cap is PER BODY, not per
 * session — a session of many capped-but-large tabs can still exceed the origin
 * quota, which `guardedJsonStorage` handles (last good snapshot + toast).
 *
 * Every tab type that persists a verbatim user body follows the same rule:
 *   - query tab — `query: ''` + `queryOmitted: true`, keeping `filePath`
 *     (restore reloads the file) and dropping `fileLastSavedHash` (the omitted
 *     body must never look in sync with disk).
 *   - notebook tab — each oversized CELL is `source: ''` + `sourceOmitted:
 *     true`; the tab drops `lastSavedHash` for the same reason. `filePath`
 *     stays so restore can name the file in the placeholder; there is no
 *     reload-on-mount for `.ipynb`, so `tabDeserializer` detaches the restored
 *     notebook from the file instead (Save must not overwrite it).
 *   - visual-query tab (retired; still in saved sessions written by older
 *     versions, which are re-persisted as-is) — the query-tab rule: `query: ''`
 *     + `queryOmitted: true`. Restore turns it into a Query tab.
 *   - pl-sql-debugger tab — an anonymous block's `source` is the whole editor
 *     buffer: dropped, with `sourceOmitted: true`.
 *   - table-designer tab — `currentSql` is a user-editable buffer (View SQL)
 *     and `initialSql` its Save baseline. Both are omitted rather than
 *     replaced: a placeholder in `currentSql` would be diffed against the
 *     baseline and EXECUTED as DDL. `sqlOmitted: true` lets restore explain
 *     the gap while the tab re-derives its buffers.
 *   - spatial-explorer tab — a `kind: 'query'` layer's `source.sql` is run on
 *     activate, so an oversized one becomes `sql: ''` + `sqlOmitted: true` and
 *     the loader refuses to execute it; the layer stays in the list, visibly
 *     failing, instead of quietly vanishing from the map.
 *
 * Returns the same references when nothing needs capping. The per-tab-type
 * mechanics live in `sessionPersistenceCapTabs.ts` (`capTab`); this module owns
 * the policy, the session-level mapping and the restore-side predicates the
 * session serializer reads back.
 *
 * TOTALITY — this path must never throw. zustand runs `partialize` BEFORE the
 * guarded `storage.setItem`, so anything thrown here escapes `set()`
 * synchronously as an uncaught renderer crash and never reaches
 * `guardedJsonStorage`. `savedSessions` are not sanitized on rehydrate either
 * (the store's `migrate` only strips explorer state), so ONE malformed
 * persisted tab — `cells: [null]`, a missing `content` — would re-throw on
 * every later session write: a crash loop the user cannot leave without
 * clearing storage. Every capper therefore validates the shape it reads (the
 * same `isRecord` defensiveness `tabDeserializer` applies on the way back in),
 * and `capTab` catches anything unanticipated and degrades to "persist the tab
 * as-is" — the guarded storage then handles the size.
 */

import type { Session } from '../types/sessionTypes';
import { buildOmittedScriptPlaceholder } from '../../../types/editorTextLimits';
import { capTab } from './sessionPersistenceCapTabs';

/**
 * True while a tab restored with an omitted script is still exactly what the
 * restore produced: detached from any file and showing the untouched
 * placeholder for `pendingFileReload`. Snapshot writers (session serializer,
 * closed-tab history) may then record the omitted file-tied shape; once the
 * user typed or opened another file here, the tab's real content wins.
 */
export function isUntouchedPendingReload(content: {
  query: string;
  filePath?: string;
  pendingFileReload?: string;
}): content is { query: string; filePath?: undefined; pendingFileReload: string } {
  return (
    typeof content.pendingFileReload === 'string' &&
    !content.filePath &&
    content.query === buildOmittedScriptPlaceholder(content.pendingFileReload)
  );
}

/**
 * True while a restored Table Designer tab is still exactly what the cap left
 * behind: its draft was dropped and the user has not typed their own
 * definition since. Unlike the two predicates above there is no text to
 * compare — the Designer has a single write seam for every buffer edit
 * (`onBufferChange`, used by both the SQL view and the visual editor) and it
 * clears the flag there, so the flag alone IS "untouched". The session
 * serializer re-emits `sqlOmitted` while this holds, otherwise the banner
 * would vanish at the first autosave and the user would never learn their
 * draft was discarded.
 */
export function isUntouchedOmittedDesignerDraft(content: { sqlOmitted?: boolean }): boolean {
  return content.sqlOmitted === true;
}

export function capSessionForPersistence<T extends Session | null | undefined>(session: T): T {
  if (!session || !Array.isArray(session.tabs)) return session;
  let changed = false;
  const tabs = session.tabs.map((tab) => {
    const capped = capTab(tab);
    if (capped !== tab) changed = true;
    return capped;
  });
  return changed ? ({ ...session, tabs } as T) : session;
}

export function capSessionsForPersistence(sessions: Session[]): Session[] {
  if (!Array.isArray(sessions)) return sessions;
  let changed = false;
  const capped = sessions.map((session) => {
    const next = capSessionForPersistence(session);
    if (next !== session) changed = true;
    return next;
  });
  return changed ? capped : sessions;
}
